export const parsePresentationDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  if (raw === '') {
    return null;
  }

  // Keep YYYY-MM-DD as local date to avoid UTC timezone shift.
  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    const localDate = new Date(year, month - 1, day);
    return Number.isNaN(localDate.getTime()) ? null : localDate;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  // Fallback for DB-like datetime strings: YYYY-MM-DD HH:mm:ss
  const normalized = raw.replace(' ', 'T');
  const parsedNormalized = new Date(normalized);
  return Number.isNaN(parsedNormalized.getTime()) ? null : parsedNormalized;
};

export const buildTodayDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const buildCurrentMonthDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  return [new Date(year, month, 1), new Date(year, month + 1, 0)];
};

export const buildCurrentMonthApiRange = () => {
  const [start, end] = buildCurrentMonthDateRange();

  return {
    date_from: formatDateForApi(start),
    date_to: formatDateForApi(end),
  };
};

export const formatDateLong = (
  value,
  {
    fallback = '-',
    locale = 'es-MX',
  } = {}
) => {
  const date = parsePresentationDate(value);
  if (!date) {
    return fallback;
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

export const formatDateShort = (
  value,
  {
    fallback = '-',
    locale = 'es-MX',
  } = {}
) => {
  const date = parsePresentationDate(value);
  if (!date) {
    return fallback;
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const formatDateShortMonth = (
  value,
  {
    fallback = '-',
    locale = 'es-MX',
  } = {}
) => {
  const date = parsePresentationDate(value);
  if (!date) {
    return fallback;
  }

  const day = new Intl.DateTimeFormat(locale, { day: '2-digit' }).format(date);
  const monthRaw = new Intl.DateTimeFormat(locale, { month: 'short' })
    .format(date)
    .replace('.', '')
    .trim();
  const month = `${monthRaw.charAt(0).toUpperCase()}${monthRaw.slice(1, 3)}`;
  const year = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(date);

  return `${day}/${month}/${year}`;
};

export const formatMonthYearLabel = (
  value,
  {
    fallback = '',
    locale = 'es-MX',
  } = {}
) => {
  const date = parsePresentationDate(value);
  if (!date) {
    return fallback;
  }

  const monthRaw = new Intl.DateTimeFormat(locale, { month: 'long' })
    .format(date)
    .replace('.', '')
    .trim();

  const month = monthRaw
    ? `${monthRaw.charAt(0).toUpperCase()}${monthRaw.slice(1)}`
    : '';
  const year = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(date);

  if (month === '') {
    return fallback;
  }

  return `${month} ${year}`.trim();
};

export const formatDateTime = (
  value,
  {
    fallback = '-',
    locale = 'es-MX',
  } = {}
) => {
  const date = parsePresentationDate(value);
  if (!date) {
    const raw = String(value || '').trim();
    return raw === '' ? fallback : raw;
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const formatDateTimeShort = (
  value,
  {
    fallback = '-',
    locale = 'es-MX',
  } = {}
) => {
  const date = parsePresentationDate(value);
  if (!date) {
    const raw = String(value || '').trim();
    return raw === '' ? fallback : raw;
  }

  const formatted = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

  return formatted.replace(',', '');
};

export const formatRelativeDaysFromNow = (value, { fallback = '-' } = {}) => {
  const date = parsePresentationDate(value);
  if (!date) {
    return fallback;
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

  if (diffDays === 0) {
    return 'Hoy';
  }

  if (diffDays === 1) {
    return 'Hace 1 día';
  }

  return `Hace ${diffDays} días`;
};

export const formatDateForApi = (
  value,
  {
    emptyValue = '',
    allowString = false,
  } = {}
) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const raw = String(value ?? '').trim();
  if (allowString && raw !== '') {
    return raw;
  }

  return emptyValue;
};
